import { useState } from 'react';
import { Bed, Bath, Maximize2, Eye, ArrowRight, MapPin } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import type { PropertyUnit } from '@/lib/units';
import { formatCurrency, formatFloorDisplay, getStatusBadgeClasses } from '@/lib/utils';
import { UnitLocationMap } from './UnitLocationMap';

interface UnitCardProps {
  unit: PropertyUnit;
  onQuickView: (unit: PropertyUnit) => void;
  onViewDetails: (unit: PropertyUnit) => void;
}

export const UnitCard = ({ unit, onQuickView, onViewDetails }: UnitCardProps) => {
  const [showMap, setShowMap] = useState(false);
  
  const formatArea = (area: number | null) => {
    if (!area) return 'N/A';
    return `${area.toLocaleString()} sq.ft`;
  };

  const hasImage = unit.images && unit.images.length > 0;
  const imageUrl = hasImage ? unit.images[0] : '/placeholder.svg';

  return (
    <Card className="group hover:shadow-lg transition-all duration-300 overflow-hidden animate-fade-in-scale">
      <div className="relative aspect-[4/3] overflow-hidden bg-muted">
        <img
          src={imageUrl}
          alt={`Unit ${unit.unit_number}`}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          onError={(e) => {
            (e.target as HTMLImageElement).src = '/placeholder.svg';
          }}
        />
        <div className="absolute top-4 right-4 flex flex-col gap-2">
          <Badge
            className={getStatusBadgeClasses(unit.availability) + ' shadow-lg'}
          >
            {unit.availability.charAt(0).toUpperCase() + unit.availability.slice(1)}
          </Badge>
        </div>
        <div className="absolute bottom-4 left-4">
          <Badge variant="outline" className="bg-background/90 backdrop-blur-sm">
            {unit.view_type === 'lagoon' ? 'Lagoon View' : 'Canal View'}
          </Badge>
        </div>
      </div>

      {/* Desktop: Always visible location map */}
      <div className="hidden md:block border-t bg-muted/30 py-2">
        <UnitLocationMap 
          unitNumber={unit.unit_number}
          compact
          showTitle
        />
      </div>

      {/* Mobile: Toggle button */}
      <button
        className="md:hidden w-full border-t py-2 flex items-center justify-center gap-1 text-sm text-muted-foreground hover:bg-muted/50 transition-colors"
        onClick={() => setShowMap(!showMap)}
      >
        <MapPin className="w-4 h-4" />
        {showMap ? 'Hide' : 'Show'} Location
      </button>
      {showMap && (
        <div className="md:hidden border-t bg-muted/30 py-2">
          <UnitLocationMap unitNumber={unit.unit_number} compact showTitle />
        </div>
      )}

      <CardContent className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="font-display text-2xl font-bold mb-1">Unit {unit.unit_number}</h3>
            <p className="text-sm text-muted-foreground">{formatFloorDisplay(unit.floor)}</p>
          </div>
          <Badge variant="secondary" className="bg-brand-teal/20 text-brand-teal border-brand-teal">
            {unit.category}
          </Badge>
        </div>

        <div className="flex items-center gap-4 mb-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-1">
            <Bed className="w-4 h-4" />
            <span>{unit.bedrooms}</span>
          </div>
          <div className="flex items-center gap-1">
            <Bath className="w-4 h-4" />
            <span>{unit.bathrooms}</span>
          </div>
          <div className="flex items-center gap-1">
            <Maximize2 className="w-4 h-4" />
            <span>{formatArea(unit.area_total)}</span>
          </div>
        </div>

        <div className="flex items-center gap-2 mb-4">
          <Badge variant="outline" className="text-xs">
            {unit.view_type === 'lagoon' ? 'Lagoon View' : 'Canal View'}
          </Badge>
        </div>

        {unit.show_price && unit.price ? (
          <div className="mb-4">
            <p className="text-2xl font-bold text-brand-teal">
              {formatCurrency(unit.price)}
            </p>
          </div>
        ) : (
          <div className="mb-4 p-3 rounded-lg border border-brand-teal/30 bg-brand-teal/5">
            <div className="flex items-center gap-2 text-brand-teal mb-1">
              <span className="text-lg">💎</span>
              <span className="font-semibold">Price on Request</span>
            </div>
            <p className="text-xs text-muted-foreground">
              Contact us for exclusive pricing
            </p>
          </div>
        )}

        <div className="flex flex-col gap-2">
          <Button
            variant="outline"
            className="w-full"
            onClick={() => onQuickView(unit)}
          >
            <Eye className="w-4 h-4 mr-2" />
            Quick View
          </Button>
          <Button
            className="w-full bg-brand-teal hover:bg-brand-navy text-white"
            onClick={() => onViewDetails(unit)}
          >
            View Details
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

